import { ColumnDef } from "@tanstack/react-table";
import { Spinner } from "components";
import { DataTable } from "components/Table/DataTable";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  applicationStatus,
  dueTone,
  timeAgo,
  registrationLine,
  type ApplicationStatus,
  type BusinessListItem,
  APPLICATIONS_LIMIT,
} from "service/business";
import { linkButton } from "pages/ApplicationReview/styles";
import { DueChip, StatusChip } from "./StatusChip";

type Props = {
  rows: BusinessListItem[] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  search: string;
  statuses: ApplicationStatus[];
};

const rowStatus = (b: BusinessListItem) => applicationStatus(b.state);
const dueFor = (b: BusinessListItem) =>
  rowStatus(b) === "waiting" ? dueTone(b.oldestDueAt) : null;

function RowAction({
  b,
  onOpen,
}: Readonly<{ b: BusinessListItem; onOpen: (b: BusinessListItem) => void }>) {
  return (
    <button
      type="button"
      className={linkButton}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(b);
      }}
    >
      Open
    </button>
  );
}

export function ApplicationsTable({
  rows,
  loading,
  error,
  onRetry,
  search,
  statuses,
}: Readonly<Props>) {
  const navigate = useNavigate();

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows ?? []).filter(
      (b) =>
        (!q || b.legalName.toLowerCase().includes(q)) &&
        (statuses.length === 0 || statuses.includes(rowStatus(b))),
    );
  }, [rows, search, statuses]);

  const open = useCallback((b: BusinessListItem) => navigate(`/applications/${b.id}`), [navigate]);

  const columns = useMemo<ColumnDef<BusinessListItem>[]>(
    () => [
      {
        id: "name",
        accessorKey: "legalName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Company Name" />
        ),
        cell: ({ row }) => (
          <div>
            <p>{row.original.legalName}</p>
            <p className="text-xs text-[#6B7280]">
              Registration · PAN {row.original.pan}
            </p>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "type",
        header: ({ column }) => (
          <DataTableColumnHeader className="text-center" column={column} title="Type" />
        ),
        cell: () => <p className="text-center w-full">Registration</p>,
        enableSorting: false,
      },
      {
        id: "submitted",
        header: ({ column }) => (
          <DataTableColumnHeader className="text-center" column={column} title="Submitted" />
        ),
        cell: ({ row }) => (
          <p className="text-center w-full">
            {row.original.submittedAt ? timeAgo(row.original.submittedAt) : "—"}
          </p>
        ),
        enableSorting: false,
      },
      {
        id: "sla",
        header: ({ column }) => (
          <DataTableColumnHeader className="text-center" column={column} title="SLA" />
        ),
        cell: ({ row }) => {
          const tone = dueFor(row.original);
          return (
            <div className="text-center w-full">
              {tone ? <DueChip tone={tone} /> : "—"}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader className="text-center" column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <div className="text-center w-full">
            <StatusChip status={rowStatus(row.original)} />
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "action",
        header: ({ column }) => (
          <DataTableColumnHeader className="text-center" column={column} title="Action" />
        ),
        cell: ({ row }) => (
          <div className="text-center w-full">
            <RowAction b={row.original} onOpen={open} />
          </div>
        ),
        enableSorting: false,
      },
    ],
    [open],
  );

  if (error) {
    return (
      <div className="p-6 text-sm text-[#333333]" role="alert">
        Couldn&apos;t load applications.{" "}
        <button type="button" className={linkButton} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (loading) return <Spinner />;

  if ((rows ?? []).length === 0) {
    return (
      <div className="p-6 text-sm text-[#333333]">
        <p className="font-semibold">Nothing waiting for review.</p>
        <p className="text-[#6B7280]">
          New applications appear here once a business submits.
        </p>
      </div>
    );
  }
  if (visible.length === 0) {
    return (
      <p className="p-6 text-sm text-[#333333]">
        No applications match &quot;{search}&quot;.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <h1 className="sr-only">Applications</h1>
      {(rows ?? []).length >= APPLICATIONS_LIMIT && (
        <p className="text-xs text-[#6B7280]">Showing the latest {APPLICATIONS_LIMIT}</p>
      )}
      <div className="sm:hidden">
        <DataTable
          columns={columns}
          data={visible}
          onRowClick={open}
          isToolbarVisible={false}
          isPaginationVisible={true}
          isPageCountVisible={false}
        />
      </div>
      <ul className="hidden sm:flex flex-col gap-2 overflow-auto">
        {visible.map((b) => {
          const tone = dueFor(b);
          return (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => open(b)}
                className="w-full text-left border rounded-lg p-3 flex flex-col gap-2 cursor-pointer hover:bg-[#E1EBFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
              >
                <span className="text-sm font-semibold">{b.legalName}</span>
                <span className="text-xs text-[#6B7280]">
                  {registrationLine(b)}
                </span>
                <span className="flex gap-2 items-center">
                  <StatusChip status={rowStatus(b)} />
                  {tone && <DueChip tone={tone} />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
